import * as R from 'ramda';
import { ComponentIds, ComponentUtils, HierarchyUtils, RoomPartType, TransformUtils, Vector } from '@inter-ikea-core/core';
import { getCornerRelationSurfaces, hasWallComponent, requireEdgeRelationComponent } from '@inter-ikea-kvadrat/kvadrat';
import { CardinalDirection } from '@inter-ikea-betrakta/viewer';
export const sortByWallPosition = (frames, frameSortOrder) => {
    const leftWallFrames = frames.filter(frame => {
        return frame.wallPosition === 'left';
    });
    const frontWallFrames = frames.filter(frame => {
        return frame.wallPosition === 'front';
    });
    const rightWallFrames = frames.filter(frame => {
        return frame.wallPosition === 'right';
    });
    const backWallFrames = frames.filter(frame => {
        return frame.wallPosition === 'back';
    });
    const sortedLeftWallFrames = R.sortWith([R.descend(R.path(['position', 'z']))])(leftWallFrames);
    const sortedFrontWallFrames = R.sortWith([R.ascend(R.path(['position', 'x']))])(frontWallFrames);
    const sortedRightWallFrames = R.sortWith([R.ascend(R.path(['position', 'z']))])(rightWallFrames);
    const sortedBackWallFrames = R.sortWith([R.ascend(R.path(['position', 'x']))])(backWallFrames);
    const result = [
        ...sortedLeftWallFrames,
        ...sortedFrontWallFrames,
        ...sortedRightWallFrames,
        ...sortedBackWallFrames
    ].filter(Boolean);
    return frameSortOrder === 'rtl' ? result.reverse() : result;
};
/**
 * Returns the entities with its viewerObjectId and what wall entities it is connected to.
 * For frames connected to wall floor edge it returns the corresponding wall entity.
 * For frames connected to wall corner it returns both wall entities for that corner.
 */
export const getEntitiesWithWallPosition = (entities) => {
    return entities.map(entity => {
        const roomParent = HierarchyUtils.findWithComponentInUpwardsHierarchy(entity, ComponentIds.ROOM_PART_COMPONENT_ID);
        const part = roomParent?.getComponent(ComponentIds.ROOM_PART_COMPONENT_ID);
        const connectedToWallEntities = [];
        if (roomParent && part?.type === RoomPartType.WALL) {
            connectedToWallEntities.push(roomParent);
        }
        else if (roomParent && part?.type === RoomPartType.EDGE) {
            const { surface1, surface2 } = requireEdgeRelationComponent(roomParent);
            const wallEntity = [surface1, surface2].find(e => hasWallComponent(e));
            wallEntity && connectedToWallEntities.push(wallEntity);
        }
        else if (roomParent && part?.type === RoomPartType.CORNER) {
            const surfaces = getCornerRelationSurfaces(roomParent);
            const wallEntities = surfaces.filter(e => hasWallComponent(e));
            connectedToWallEntities.push(...wallEntities);
        }
        const allEntities = [entity, ...HierarchyUtils.descendants(entity)];
        const entityObjectIds = allEntities
            .map(entity => entity
            .getComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
            ?.viewerObject.getViewerObjectId())
            .filter(Boolean);
        return {
            entity,
            entityObjectIds,
            connectedToWallEntities
        };
    });
};
/**
 * Returns the entities with its viewerObjectId and what wall entities it is connected to as a map with wall entity id as key.
 */
export const getEntitiesWithWallPositionByWall = (entitiesWithWallPostion) => {
    return entitiesWithWallPostion.reduce((acc, e) => {
        e.connectedToWallEntities.forEach(wallEntity => {
            const id = ComponentUtils.displayName(wallEntity) || '';
            if (!acc[id]) {
                acc[id] = { wallEntity: wallEntity, entitiesWithPosition: [] };
            }
            acc[id].entitiesWithPosition.push(e);
        });
        return acc;
    }, {});
};
/**
 * Returns the cardinal direction for the wall entity.
 */
export const getCardinalDirectionForWall = (wallEntity) => {
    const wallTransform = TransformUtils.worldTransform(wallEntity).up;
    return wallTransform.equals(new Vector(-1, 0, 0))
        ? CardinalDirection.LEFT
        : wallTransform.equals(new Vector(1, 0, 0))
            ? CardinalDirection.RIGHT
            : wallTransform.equals(new Vector(0, 0, -1))
                ? CardinalDirection.BACK
                : CardinalDirection.FRONT;
};
//# sourceMappingURL=wallPosition.js.map